import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';
import { 
  ArrowLeft, 
  Music, 
  Calendar, 
  MapPin,
  Play,
  Pause,
  User,
  Facebook,
  Instagram,
  ExternalLink
} from 'lucide-react';
import api from '../services/api';
import PresignedImage from '../components/PresignedImage';

const Artist = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { playTrack, currentTrack, isPlaying, playPause } = useMusicPlayer();
  const [artist, setArtist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArtistData = useCallback(async () => {
    try {
      const artistResponse = await api.get(`/api/artists/${artistId}`);
      setArtist(artistResponse.data.artist);

      const tracksResponse = await api.get(`/api/artists/${artistId}/tracks`);
      setTracks(tracksResponse.data.tracks);
    } catch (error) {
      console.error('Failed to fetch artist data:', error);
    } finally {
      setLoading(false);
    }
  }, [artistId]);

  useEffect(() => {
    fetchArtistData();
  }, [fetchArtistData]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-50"></div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p className="text-gray-400">Artist not found</p>
      </div>
    );
  }

  return (
    <div className="app-screen min-h-screen text-white font-inter">
      <div className="space-y-6">
        {/* Header */}
        <div className="relative bg-black/90 rounded-lg p-6 shadow-[0_4px_4px_rgba(0,0,0,0.17)]">
          <div className="relative">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-white">{artist.artist_name}</h1>
                  <p className="text-gray-400">Artist Profile</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Artist Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Artist Details */}
          <div className="lg:col-span-1">
            <div className="relative bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)] p-6">
              <div className="relative">
                <h2 className="text-lg font-semibold text-white mb-4">Artist Information</h2>
                
                <div className="mb-4">
                  {artist.image ? (
                    <div className="w-full rounded-lg overflow-hidden">
                      <PresignedImage
                        assetKey={artist.image}
                        assetType="artist"
                        alt={artist.artist_name}
                        className="w-full h-auto object-cover rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-gray-700 rounded-lg flex items-center justify-center">
                      <User className="h-24 w-24 text-gray-400" />
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Legal Name */}
                  <div>
                    <span className="text-xs font-bold text-gray-400 uppercase block mb-1">Legal Name</span>
                    <p className="text-sm text-white">{artist.artist_legal_name || 'N/A'}</p>
                  </div>

                  {/* Bio */}
                  {artist.bio && (
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase block mb-1">Biography</span>
                      <p className="text-sm text-gray-300 leading-relaxed">{artist.bio}</p>
                    </div>
                  )}
                  
                  {/* Location */}
                  {artist.location && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-300">{artist.location}</span>
                    </div>
                  )}
                  
                  {/* Formed Date */}
                  {artist.formed_date && (
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-300">
                        Formed {new Date(artist.formed_date).getFullYear()}
                      </span>
                    </div>
                  )}

                  {/* Social Media Links */}
                  {(artist.artist_fb_url || artist.artist_ig_url) && (
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase block mb-2">Social Media</span>
                      <div className="flex space-x-4">
                        {artist.artist_fb_url && (
                          <a
                            href={artist.artist_fb_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 border border-white/20 text-sm font-medium rounded-md text-white bg-blue-600/20 hover:bg-blue-600/30 transition-colors"
                          >
                            <Facebook className="h-4 w-4 mr-2" />
                            Facebook
                            <ExternalLink className="h-3 w-3 ml-2" />
                          </a>
                        )}
                        {artist.artist_ig_url && (
                          <a
                            href={artist.artist_ig_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 border border-white/20 text-sm font-medium rounded-md text-white bg-pink-600/20 hover:bg-pink-600/30 transition-colors"
                          >
                            <Instagram className="h-4 w-4 mr-2" />
                            Instagram
                            <ExternalLink className="h-3 w-3 ml-2" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* System Info */}
                  <div className="pt-4 border-t border-white/20">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-400">Added:</span>
                        <span className="ml-2 text-white">
                          {formatDate(artist.created_at)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Last Updated:</span>
                        <span className="ml-2 text-white">
                          {formatDate(artist.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tracks by Artist */}
          <div className="lg:col-span-2">
            <div className="relative bg-black/90 rounded-lg shadow-[0_4px_4px_rgba(0,0,0,0.17)]">
              <div className="relative">
                <div className="px-6 py-4 border-b border-white/20">
                  <h2 className="text-lg font-semibold text-white">
                    Tracks by {artist.artist_name}
                  </h2>
                  <p className="text-sm text-gray-400">
                    {tracks.length} track{tracks.length !== 1 ? 's' : ''}
                  </p>
                </div>
                
                <div className="divide-y divide-white/10">
                  {tracks.length > 0 ? (
                    tracks.map((track) => (
                      <div key={track.id} className="px-6 py-4 hover:bg-white/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {/* Track Info */}
                            <div>
                              <h3 className="text-sm font-medium text-white">
                                {track.title || 'Untitled'}
                              </h3>
                              <div className="text-xs text-gray-400">
                                {track.release_package && `${track.release_package} • `}
                                {track.genre && `${track.genre}`}
                                {track.bpm && ` • ${track.bpm} BPM`}
                                {track.url && ` • URL: ${track.url}`}
                              </div>
                            </div>
                          </div>
                          
                          {/* Play/Pause Button */}
                          {track.url ? (
                            currentTrack && currentTrack.id === track.id && isPlaying ? (
                              <button
                                onClick={playPause}
                                className="p-2 bg-gold-50 hover:bg-gold-100 text-white rounded-full transition-colors"
                                title="Pause track"
                              >
                                <Pause className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  console.log('🎵 Play button clicked for track:', track);
                                  playTrack(track, tracks);
                                }}
                                className="p-2 text-gold-50 hover:text-gold-100 hover:bg-gold-50/20 rounded-full transition-colors"
                                title="Play track"
                              >
                                <Play className="h-4 w-4" />
                              </button>
                            )
                          ) : (
                            <div className="p-2 text-gray-500 text-xs">
                              No audio
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-6 py-8 text-center">
                      <Music className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-400">No tracks found for this artist</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Artist;
